/**
 * Offline IndexedDB – TGAone Baustellen-Modus
 * Stores: syncQueue, cachedData, offlinePhotos
 */
import { openDB } from "idb";

const DB_NAME = "tgaone-offline";
const DB_VERSION = 1;

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Queue for pending mutations (POST/PATCH/DELETE)
        if (!db.objectStoreNames.contains("syncQueue")) {
          const sq = db.createObjectStore("syncQueue", { keyPath: "id" });
          sq.createIndex("by-timestamp", "timestamp");
          sq.createIndex("by-status", "status");
        }
        // Cached API responses for offline reads
        if (!db.objectStoreNames.contains("cachedData")) {
          db.createObjectStore("cachedData", { keyPath: "key" });
        }
        // Offline photo blobs
        if (!db.objectStoreNames.contains("offlinePhotos")) {
          const ph = db.createObjectStore("offlinePhotos", { keyPath: "id" });
          ph.createIndex("by-status", "status");
        }
      },
    });
  }
  return dbPromise;
}

// ── Sync Queue ──
export async function addToQueue(entry) {
  const db = await getDB();
  const item = {
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    status: "pending", // pending | syncing | done | failed
    retries: 0,
    ...entry, // { type, endpoint, method, body, meta }
  };
  await db.put("syncQueue", item);
  return item;
}

export async function getQueueItems(status = null) {
  const db = await getDB();
  if (status) {
    return db.getAllFromIndex("syncQueue", "by-status", status);
  }
  return db.getAll("syncQueue");
}

export async function updateQueueItem(id, updates) {
  const db = await getDB();
  const item = await db.get("syncQueue", id);
  if (!item) return;
  Object.assign(item, updates);
  await db.put("syncQueue", item);
  return item;
}

export async function removeQueueItem(id) {
  const db = await getDB();
  await db.delete("syncQueue", id);
}

export async function clearCompletedQueue() {
  const db = await getDB();
  const done = await db.getAllFromIndex("syncQueue", "by-status", "done");
  const tx = db.transaction("syncQueue", "readwrite");
  for (const item of done) {
    await tx.store.delete(item.id);
  }
  await tx.done;
}

export async function getPendingCount() {
  const db = await getDB();
  const pending = await db.getAllFromIndex("syncQueue", "by-status", "pending");
  return pending.length;
}

// ── Cached Data ──
export async function setCachedData(key, data) {
  const db = await getDB();
  await db.put("cachedData", { key, data, updatedAt: new Date().toISOString() });
}

export async function getCachedData(key) {
  const db = await getDB();
  const entry = await db.get("cachedData", key);
  return entry?.data ?? null;
}

export async function removeCachedData(key) {
  const db = await getDB();
  await db.delete("cachedData", key);
}

// ── Offline Photos ──
export async function storeOfflinePhoto(entry) {
  const db = await getDB();
  const item = {
    id: `ph_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    status: "pending",
    ...entry, // { blob, filename, projectId, taskId }
  };
  await db.put("offlinePhotos", item);
  return item;
}

export async function getPendingPhotos() {
  const db = await getDB();
  return db.getAllFromIndex("offlinePhotos", "by-status", "pending");
}

export async function updatePhoto(id, updates) {
  const db = await getDB();
  const item = await db.get("offlinePhotos", id);
  if (!item) return;
  Object.assign(item, updates);
  await db.put("offlinePhotos", item);
}

export async function removePhoto(id) {
  const db = await getDB();
  await db.delete("offlinePhotos", id);
}

export async function getPendingPhotoCount() {
  const db = await getDB();
  const pending = await db.getAllFromIndex("offlinePhotos", "by-status", "pending");
  return pending.length;
}
